// client/src/components/notifications/NotificationsPage.js
import './NotificationsPage.css'
import { useNavigate } from 'react-router-dom'
import useNotificationsList from '../../hooks/useNotificationsList'
import PageHeader from '../common/layout/PageHeader'
import {
  formatNotificationText,
  formatRelativeTime,
  getNotificationNavigationPath,
} from '../../utils/notificationFormatter'

export default function NotificationsPage() {
  const navigate = useNavigate()
  const {
    notifications,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotificationsList()

  const handleNotificationClick = (notification) => {
    // Get navigation path first
    const path = getNotificationNavigationPath(notification)

    // Mark as read (non-blocking mutation)
    if (!notification.read_at) {
      markAsRead(notification.id)
    }

    // Navigate to relevant page if applicable
    if (path) {
      navigate(path)
    }
  }

  const handleMarkAllAsRead = () => {
    markAllAsRead()
  }

  const handleDeleteNotification = (notificationId, event) => {
    // Stop event propagation to prevent navigation
    event.stopPropagation()
    deleteNotification(notificationId)
  }

  const handleMarkAsRead = (notificationId, event) => {
    // Stop event propagation to prevent navigation
    event.stopPropagation()
    markAsRead(notificationId)
  }

  if (loading) {
    return (
      <div className="notifications-container">
        <PageHeader title="Notifications" showBack={true} />
        <div className="notifications-loading">Loading notifications...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="notifications-container">
        <PageHeader title="Notifications" showBack={true} />
        <div className="notifications-error">
          Error loading notifications: {error?.message || 'Unknown error'}
        </div>
      </div>
    )
  }

  const hasUnread = notifications.some((n) => !n.read_at)

  return (
    <div className="notifications-container">
      <PageHeader title="Notifications" showBack={true} />

      <main className="notifications-content">
        {notifications.length === 0 ? (
          <div className="notifications-empty-state">
            <div className="empty-state-icon" aria-hidden="true">
              🔔
            </div>
            <h2>No Notifications</h2>
            <p>You're all caught up! Check back later for updates.</p>
          </div>
        ) : (
          <>
            {hasUnread && (
              <div className="notifications-header">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleMarkAllAsRead}
                  data-testid="mark-all-read-button"
                >
                  Mark all as read
                </button>
              </div>
            )}

            <div className="notifications-list" role="list">
              {notifications.map((notification) => {
                const isUnread = !notification.read_at
                const text = formatNotificationText(notification)
                const time = formatRelativeTime(notification.created_at)
                const isClickable =
                  !!getNotificationNavigationPath(notification)

                return (
                  <div
                    key={notification.id}
                    className={`notification-item ${isUnread ? 'notification-item--unread' : ''} ${isClickable ? 'notification-item--clickable' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                    onKeyDown={(e) => {
                      if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        handleNotificationClick(notification)
                      }
                    }}
                    role="listitem"
                    tabIndex={isClickable ? 0 : -1}
                    aria-label={`${text}. ${time}. ${isUnread ? 'Unread' : 'Read'}`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="notification-content">
                      {isUnread && (
                        <span
                          className="notification-unread-indicator"
                          aria-label="Unread"
                        />
                      )}
                      <div className="notification-text">{text}</div>
                      <div className="notification-time">{time}</div>
                    </div>
                    {isUnread && (
                      <button
                        className="notification-mark-read-button"
                        onClick={(e) => handleMarkAsRead(notification.id, e)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            e.stopPropagation()
                            handleMarkAsRead(notification.id, e)
                          }
                        }}
                        aria-label="Mark as read"
                        data-testid={`mark-read-${notification.id}`}
                        title="Mark as read"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="notification-delete-button"
                      onClick={(e) =>
                        handleDeleteNotification(notification.id, e)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          handleDeleteNotification(notification.id, e)
                        }
                      }}
                      aria-label="Delete notification"
                      data-testid={`delete-notification-${notification.id}`}
                      title="Delete notification"
                    >
                      🗑️
                    </button>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </main>
    </div>
  )
}
